/**
 * What needs a grown-up right now, as two small numbers for the tab bar.
 *
 * The tab bar shows a badge only where it means "something is waiting on
 * you": an unanswered ask against the tank, and a wish whose wait is over.
 * A badge for the whole waiting list would be a number that never goes to
 * zero, and a badge that never clears is one nobody reads (tab-badge).
 *
 * This deliberately does not reuse `useFuel` / `useWishes`. Those load every
 * entry and every wish so their screens can project balances; the tab bar
 * wants two counts on every screen and should not pay for the ledger each
 * time. Two narrow selects, no domain projection — `isReady` is the only
 * rule applied, and it is the domain's, not a copy.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from './supabase.js';
import { isReady } from '../domain/waiting.js';
import { toDateOnly } from '../domain/periods.js';

const NONE = Object.freeze({ asks: 0, readyWishes: 0 });

export function useAttention(familyId, enabled) {
  const [counts, setCounts] = useState(NONE);
  // Re-count on every navigation. It is the cheapest signal that something
  // may have changed: a parent who just approved an ask lands somewhere else
  // afterwards, and the badge should already be gone when they get there.
  const { pathname } = useLocation();

  const load = useCallback(async () => {
    if (!familyId || !enabled) { setCounts(NONE); return; }
    const today = toDateOnly(new Date());
    const [askRes, wishRes] = await Promise.all([
      supabase.from('spend_proposals')
        .select('id', { count: 'exact', head: true })
        .eq('family_id', familyId)
        .in('status', ['proposed', 'seconded']),
      supabase.from('wishes')
        .select('id, status, ready_on, waived_at')
        .eq('family_id', familyId)
        .eq('status', 'waiting'),
    ]);
    // A failed count is an absent badge, not an error banner: the screens
    // behind the tabs report their own problems with more room to say so.
    if (askRes.error || wishRes.error) { setCounts(NONE); return; }
    const readyWishes = (wishRes.data ?? [])
      .filter((w) => isReady({ status: w.status, readyOn: w.ready_on, waivedAt: w.waived_at }, today))
      .length;
    setCounts({ asks: askRes.count ?? 0, readyWishes });
  }, [familyId, enabled]);

  useEffect(() => { load(); }, [load, pathname]);

  return counts;
}
